import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { UserWithStats } from '@/shared/db';

interface UserTableProps {
  users: UserWithStats[];
  onSuspend: (userId: string) => void;
  onActivate: (userId: string) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onSuspend,
  onActivate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-romance-200">
        <thead className="bg-romance-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.user')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.type')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.country')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.plan')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.documents')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.status')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
              {t('admin.users.table.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-romance-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-romance-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-juridico">
                    {user.full_name || t('common.notAvailable')}
                  </div>
                  <div className="text-sm text-juridico/60">{user.email}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-juridico capitalize">
                  {user.user_type || t('common.notAvailable')}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-juridico">{user.country || t('common.notAvailable')}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.plan === 'pro'
                      ? 'bg-tiber-500/10 text-tiber-500'
                      : 'bg-tiber-100 text-tiber-800'
                  }`}
                >
                  {user.plan}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico">
                {user.document_count || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.is_active
                      ? 'bg-atlantis-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {user.is_active ? t('admin.users.active') : t('admin.users.suspended')}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    className="text-tiber-500 hover:text-tiber-500/80 transition-colors"
                    title={t('admin.users.actions.view')}
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                  {user.is_active ? (
                    <button
                      onClick={() => onSuspend(user.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title={t('admin.users.actions.suspend')}
                    >
                      <XCircleIcon className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => onActivate(user.id)}
                      className="text-atlantis-600 hover:text-green-800 transition-colors"
                      title={t('admin.users.actions.activate')}
                    >
                      <CheckCircleIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

